import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import KkpPlanningEmptyState from './KkpPlanningEmptyState';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-planning-view-mode.scss';

const CHANNEL_ORDER = ['mb', 'edito', 'agenda', 'news_sorties', 'news_food'];
const CHANNEL_ICONS = {
  mb: 'mdi:image-filter-hdr',
  edito: 'mdi:file-document-outline',
  agenda: 'mdi:calendar-month-outline',
  news_sorties: 'mdi:email-newsletter',
  news_food: 'mdi:food-outline',
};

const METRIC_LABELS = {
  affichage: 'Vues',
  clics: 'Clics',
  taux_click: 'Taux de clic',
  news_recues: 'Mails envoyés',
  news_ouvertes: 'Mails ouverts',
  pub_cliquees: 'Pubs cliquées',
};

const METRIC_HINTS = {
  affichage: 'Nombre de fois où la pub a été vue',
  clics: 'Nombre de clics sur la pub',
  taux_click: 'Part des affichages qui ont généré un clic',
  news_recues: 'Nombre de mails reçus',
  news_ouvertes: 'Nombre de mails ouverts',
  pub_cliquees: 'Nombre de clics sur la pub dans le mail',
};

const formatValue = (value, key) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (key === 'taux_click') {
    return `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;
  }

  return Math.round(Number(value)).toLocaleString('fr-FR');
};

const formatPeriodLabel = (row) => {
  const date = String(row?.date ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const parsed = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }

  return row?.periode || '—';
};

const MetricCard = ({ title, subtitle, metrics, metricKeys, variant = 'default' }) => (
  <div className={`kkp-bilan-summary-card kkp-bilan-summary-card--${variant}`}>
    <div className="kkp-bilan-summary-card__head">
      <span className="kkp-bilan-summary-card__title">{title}</span>
      {subtitle ? <span className="kkp-bilan-summary-card__subtitle">{subtitle}</span> : null}
    </div>
    <div className="kkp-bilan-summary-card__metrics">
      {metricKeys.map((key) => (
        <div key={key} className="kkp-bilan-summary-card__metric">
          <span className="kkp-bilan-summary-card__metric-label" title={METRIC_HINTS[key]}>
            {METRIC_LABELS[key] ?? key}
          </span>
          <strong className="kkp-bilan-summary-card__metric-value">
            {formatValue(metrics?.[key], key)}
          </strong>
        </div>
      ))}
    </div>
  </div>
);

const ComparisonCell = ({ you, average, metricKey, showAverage = true }) => (
  <div className="kkp-bilan-compare-cell">
    <div className="kkp-bilan-compare-cell__you">
      {showAverage ? <span className="kkp-bilan-compare-cell__tag">Vous</span> : null}
      <strong>{formatValue(you, metricKey)}</strong>
    </div>
    {showAverage ? (
      <div className="kkp-bilan-compare-cell__avg">
        <span className="kkp-bilan-compare-cell__tag">Moyenne</span>
        <span>{formatValue(average, metricKey)}</span>
      </div>
    ) : null}
  </div>
);

const KkpBilanAudiencePreview = ({
  channels = {},
  loading = false,
  entreprise = '',
  periodeLabel = '',
  averageScope = 'all_clients',
}) => {
  const activeChannels = useMemo(
    () => CHANNEL_ORDER
      .filter((id) => channels[id]?.has_data)
      .map((id) => [id, channels[id]]),
    [channels],
  );

  const [activeTab, setActiveTab] = useState('');

  const currentTab = activeChannels.some(([id]) => id === activeTab)
    ? activeTab
    : (activeChannels[0]?.[0] ?? '');

  const currentChannel = currentTab ? channels[currentTab] : null;
  const metricKeys = currentChannel?.metric_keys ?? [];
  const averageLabel = currentChannel?.average_label ?? 'Moyenne tous clients';
  const hasEmplacement = (currentChannel?.periods ?? []).some((row) => row.emplacement);
  const periodCount = (currentChannel?.periods ?? []).length;
  const showAverage = averageScope !== 'none';

  if (!entreprise) {
    return (
      <KkpPlanningEmptyState
        message="Choisissez une entreprise pour afficher ses résultats audience."
      />
    );
  }

  if (loading) {
    return <KkpPlanningEmptyState loading />;
  }

  if (activeChannels.length === 0) {
    return (
      <KkpPlanningEmptyState
        message={`Aucune donnée trouvée pour « ${entreprise} » sur la période ${periodeLabel}.`}
      />
    );
  }

  return (
    <div className="kkp-bilan-preview">
      <div className="kkp-planning-view-toolbar mb-3">
        <div className="kkp-planning-view-switch" role="tablist" aria-label="Canaux du bilan audience">
          {activeChannels.map(([id, channel]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={currentTab === id}
              className={`kkp-planning-view-switch__btn${currentTab === id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon
                icon={CHANNEL_ICONS[id] || 'mdi:view-grid-outline'}
                className="kkp-planning-view-switch__icon"
                aria-hidden
              />
              <span>{channel.label}</span>
            </button>
          ))}
        </div>
      </div>

      {currentChannel && (
        <>
          <div className="row g-2 g-md-3 mb-3">
            {showAverage ? (
              <div className="col-md-6">
                <MetricCard
                  title={averageLabel}
                  subtitle="Référence pour comparer"
                  metrics={currentChannel.metrics?.all_clients}
                  metricKeys={metricKeys}
                  variant="avg"
                />
              </div>
            ) : null}
            <div className={showAverage ? 'col-md-6' : 'col-12'}>
              <MetricCard
                title="Votre moyenne"
                subtitle={entreprise}
                metrics={currentChannel.metrics?.client}
                metricKeys={metricKeys}
                variant="you"
              />
            </div>
          </div>

          <div className="kkp-bilan-table-wrap">
            <div className="table-responsive">
              <table className="table align-middle mb-0 kkp-bilan-table">
                <thead>
                  <tr>
                    <th scope="col" className="kkp-bilan-table__period-col">Date</th>
                    {hasEmplacement && (
                      <th scope="col" className="kkp-bilan-table__place-col">Emplacement</th>
                    )}
                    {metricKeys.map((key) => (
                      <th key={key} scope="col" className="kkp-bilan-table__metric-col">
                        <span className="kkp-bilan-table__metric-name">{METRIC_LABELS[key] ?? key}</span>
                        {METRIC_HINTS[key] ? (
                          <span className="kkp-bilan-table__metric-hint">{METRIC_HINTS[key]}</span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(currentChannel.periods ?? []).map((row, index) => (
                    <tr key={`${row.periode}-${row.emplacement ?? ''}-${index}`}>
                      <td className="kkp-bilan-table__period">
                        {formatPeriodLabel(row)}
                      </td>
                      {hasEmplacement && (
                        <td className="kkp-bilan-table__place">
                          {row.emplacement || '—'}
                        </td>
                      )}
                      {metricKeys.map((key) => (
                        <td key={`${index}-${key}`} className="kkp-bilan-table__metric">
                          <ComparisonCell
                            you={row.client?.[key]}
                            average={row.all_clients?.[key]}
                            metricKey={key}
                            showAverage={showAverage}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={hasEmplacement ? 2 : 1} className="kkp-bilan-table__foot-label">
                      Moyenne sur la période
                    </td>
                    {metricKeys.map((key) => (
                      <td key={`foot-${key}`} className="kkp-bilan-table__metric">
                        <ComparisonCell
                          you={currentChannel.metrics?.client?.[key]}
                          average={currentChannel.metrics?.all_clients?.[key]}
                          metricKey={key}
                          showAverage={showAverage}
                        />
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p className="kkp-bilan-preview__footer">
            <Icon icon="mdi:information-outline" className="me-1" aria-hidden />
            {periodCount} date{periodCount > 1 ? 's' : ''} affichée{periodCount > 1 ? 's' : ''}
            {periodeLabel ? ` · ${periodeLabel}` : ''}.
          </p>
        </>
      )}
    </div>
  );
};

export default KkpBilanAudiencePreview;
